import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box, Plus, Search, Loader2, Download, Trash2, Edit3, AlertTriangle, Wrench
} from "lucide-react";
import { FALLBACK_ASSETS } from "@/data/dummyData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const EQUIPMENT_TYPES = [
  "Computer", "Monitor", "Desk", "Chair", "Printer", "Scanner",
  "Telephone", "Shredder", "Whiteboard", "Filing Cabinet", "Projector",
  "Air Conditioner", "Fan", "Heater", "Router", "Switch", "UPS",
  "Keyboard", "Mouse", "Laptop", "Docking Station", "Webcam", "Headset",
  "Stapler", "Hole Punch", "Laminator", "Binder",
];
const STATUS_OPTS = ["Serviceable", "Not Serviceable"];
const ACTION_OPTS = ["Nothing", "Repair", "Decommission"];

const emptyForm = {
  category: "", equipment_type: "", equipment_name: "", serial_number: "",
  description: "", year_model: "", status: "Serviceable", location: "",
  assigned_name: "", assigned_appointment: "", action_needed: "Nothing",
};

export default function LogisticsInventory() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ category: "", status: "", action_needed: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [stats, setStats] = useState({ total: 0, repair_needed: 0, decommission_needed: 0 });

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;
      if (filters.action_needed) params.action_needed = filters.action_needed;
      if (search) params.search = search;
      const res = await axios.get(`${API}/logistics/inventory/assets`, { params, headers: getHeaders() });
      setAssets(res.data.length > 0 ? res.data : FALLBACK_ASSETS);
    } catch { setAssets(FALLBACK_ASSETS); toast.error("Failed to load assets. Showing demo data."); }
    setLoading(false);
  }, [filters, search]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/logistics/inventory/dashboard`, { headers: getHeaders() });
      setStats(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchAssets(); fetchStats(); }, [fetchAssets, fetchStats]);

  const openAdd = () => { setEditId(null); setForm({ ...emptyForm }); setDialogOpen(true); };
  const openEdit = (asset) => {
    setEditId(asset.id);
    setForm({
      category: asset.category || "",
      equipment_type: asset.equipment_type || "",
      equipment_name: asset.equipment_name || "",
      serial_number: asset.serial_number || "",
      description: asset.description || "",
      year_model: asset.year_model?.toString() || "",
      status: asset.status || "Serviceable",
      location: asset.location || "",
      assigned_name: asset.assigned_name || "",
      assigned_appointment: asset.assigned_appointment || "",
      action_needed: asset.action_needed || "Nothing",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.serial_number.trim()) { toast.error("Serial number is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        year_model: form.year_model ? parseInt(form.year_model) : null,
      };
      if (editId) {
        await axios.put(`${API}/logistics/inventory/assets/${editId}`, payload, { headers: getHeaders() });
        toast.success("Asset updated");
      } else {
        await axios.post(`${API}/logistics/inventory/assets`, payload, { headers: getHeaders() });
        toast.success("Asset created");
      }
      setDialogOpen(false);
      fetchAssets();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save asset");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/logistics/inventory/assets/${deleteId}`, { headers: getHeaders() });
      toast.success("Asset deleted");
      setDeleteId(null);
      fetchAssets();
      fetchStats();
    } catch { toast.error("Failed to delete"); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/logistics/inventory/export`, {
        headers: getHeaders(), responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "logistics_assets.csv"; a.click();
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
            <Box className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">INVENTORY</h1>
            <p className="text-slate-600">Equipment & Asset Tracking</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 p-4"><p className="text-2xl font-bold text-slate-800">{stats.total}</p><p className="text-xs text-slate-500">Total Assets</p></div>
        <div className="bg-white border border-amber-200 p-4"><p className="text-2xl font-bold text-amber-600">{stats.repair_needed}</p><p className="text-xs text-amber-600"><Wrench className="w-3 h-3 inline mr-1" />Needs Repair</p></div>
        <div className="bg-white border border-red-200 p-4"><p className="text-2xl font-bold text-red-600">{stats.decommission_needed}</p><p className="text-xs text-red-600"><AlertTriangle className="w-3 h-3 inline mr-1" />Decommission</p></div>
        <div className="bg-white border border-green-200 p-4"><p className="text-2xl font-bold text-green-600">{stats.serviceable || 0}</p><p className="text-xs text-green-600">Serviceable</p></div>
      </div>

      {/* Search + Filters + Actions */}
      <div className="bg-white border border-slate-200 p-4 mb-6 flex items-center gap-3 flex-wrap">
        <Search className="w-4 h-4 text-slate-400" />
        <Input placeholder="Search assets..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-48 bg-app border-slate-300 rounded-none" />
        <div className="w-px h-6 bg-slate-200" />
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-36 bg-app border-slate-300 rounded-none"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white border-slate-300 rounded-none">
            <SelectItem value="all">All Status</SelectItem>
            {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.action_needed} onValueChange={(v) => setFilters((f) => ({ ...f, action_needed: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-40 bg-app border-slate-300 rounded-none"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent className="bg-white border-slate-300 rounded-none">
            <SelectItem value="all">All Actions</SelectItem>
            {ACTION_OPTS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={openAdd} className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-none text-white">
          <Plus className="w-4 h-4 mr-1" /> Add Asset
        </Button>
        <Button onClick={handleExport} variant="outline" className="rounded-none border-slate-300">
          <Download className="w-4 h-4 mr-1" /> CSV
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
      ) : assets.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-16 text-center">
          <Box className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No assets found.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-app border-b border-slate-200">
              <tr>
                {["Equipment", "Serial #", "Category", "Status", "Action", "Location", "Assigned To", ""].map((h) => (
                  <th key={h} className="text-left p-3 font-semibold text-slate-600 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assets.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-app">
                  <td className="p-3">
                    <p className="font-medium text-slate-800">{a.equipment_name || "-"}</p>
                    <p className="text-xs text-slate-400">{a.equipment_type}</p>
                  </td>
                  <td className="p-3 text-xs font-mono text-slate-500">{a.serial_number}</td>
                  <td className="p-3"><span className="text-xs text-slate-500">{a.category || "-"}</span></td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 font-medium ${a.status === "Serviceable" ? "text-green-600 bg-green-50" : "text-red-600 bg-red-50"}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="p-3">
                    {a.action_needed && a.action_needed !== "Nothing" ? (
                      <span className={`text-xs px-2 py-0.5 font-medium ${a.action_needed === "Repair" ? "text-amber-600 bg-amber-50" : "text-red-600 bg-red-50"}`}>
                        {a.action_needed}
                      </span>
                    ) : <span className="text-xs text-slate-400">-</span>}
                  </td>
                  <td className="p-3 text-xs text-slate-500">{a.location || "-"}</td>
                  <td className="p-3">
                    <p className="text-xs text-slate-700">{a.assigned_name || "-"}</p>
                    {a.assigned_appointment && <p className="text-xs text-slate-400 font-mono">{a.assigned_appointment}</p>}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(a)} className="p-1 text-slate-400 hover:text-amber-600"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => setDeleteId(a.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">{editId ? "Edit Asset" : "Add Asset"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Input placeholder="Category (e.g. IT Equipment, Furniture)" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <Select value={form.equipment_type} onValueChange={(v) => setForm((f) => ({ ...f, equipment_type: v }))}>
              <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Type of Equipment" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-300 rounded-none max-h-60">
                {EQUIPMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Name of Equipment" value={form.equipment_name} onChange={(e) => setForm((f) => ({ ...f, equipment_name: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <Input placeholder="Serial Number *" value={form.serial_number} onChange={(e) => setForm((f) => ({ ...f, serial_number: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <textarea placeholder="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={3} />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Year Model" type="number" max={new Date().getFullYear()} value={form.year_model} onChange={(e) => setForm((f) => ({ ...f, year_model: e.target.value }))} className="rounded-none bg-app border-slate-300" />
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-300 rounded-none">
                  {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input placeholder="Location (Building + Office)" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <Input placeholder="Assigned To (Name)" value={form.assigned_name} onChange={(e) => setForm((f) => ({ ...f, assigned_name: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <Input placeholder="Appointment Number (AP-XXXXX)" value={form.assigned_appointment} onChange={(e) => setForm((f) => ({ ...f, assigned_appointment: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <Select value={form.action_needed} onValueChange={(v) => setForm((f) => ({ ...f, action_needed: v }))}>
              <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Action Needed" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-300 rounded-none">
                {ACTION_OPTS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Plus className="w-4 h-4 mr-2" /> {editId ? "Update Asset" : "Create Asset"}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white border-slate-200 rounded-none">
          <AlertDialogHeader><AlertDialogTitle>Delete Asset?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-none bg-red-600 hover:bg-red-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
